import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Search, ArrowRight, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { MandalaCorner } from '../components/luxury/MandalaCorner';
import { api } from '../App';
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('upcoming');
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    api.get(`/api/events?status=${tab}`).then(data => {
      setEvents(data.events || []);
    }).catch(() => setEvents([])).finally(() => setLoading(false));
  }, [tab]);

  const filtered = events.filter(e =>
    e.title?.toLowerCase().includes(search.toLowerCase()) ||
    e.venue?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="overflow-hidden">
      {/* Luxury Hero */}
      <section className="relative py-20 bg-gradient-to-br from-[hsl(var(--lux-royal-soft))] to-[hsl(var(--lux-ivory))] overflow-hidden">
        <MandalaCorner position="top-right" className="opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <Badge className="mb-6 bg-[hsl(var(--lux-gold-soft))] text-[hsl(var(--lux-royal))] border-[hsl(var(--lux-gold)/0.4)]">
              <Sparkles className="w-3 h-3 mr-1" /> Celebrations & Gatherings
            </Badge>
            <h1 className="text-5xl sm:text-6xl font-bold text-[hsl(var(--lux-royal))] mb-6" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="events-page-title">
              Community <span className="text-[hsl(var(--lux-gold))]">Events</span>
            </h1>
            <p className="text-lg text-[hsl(var(--lux-ink)/0.8)] max-w-2xl mx-auto">
              Discover our community celebrations, festivals, and gatherings.
            </p>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[hsl(var(--lux-gold)/0.6)] to-transparent" />
      </section>

      {/* Events listing */}
      <section className="py-12 bg-[hsl(var(--lux-ivory))]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-10">
            <Tabs value={tab} onValueChange={setTab} data-testid="events-tabs">
              <TabsList className="bg-white border border-[hsl(var(--lux-gold)/0.35)] shadow-sm">
                <TabsTrigger
                  value="upcoming"
                  className="data-[state=active]:bg-[hsl(var(--lux-royal))] data-[state=active]:text-white font-medium"
                  data-testid="events-tab-upcoming"
                >
                  Upcoming
                </TabsTrigger>
                <TabsTrigger
                  value="past"
                  className="data-[state=active]:bg-[hsl(var(--lux-royal))] data-[state=active]:text-white font-medium"
                  data-testid="events-tab-past"
                >
                  Past
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--lux-gold))]" />
              <Input
                placeholder="Search events..."
                className="pl-10 bg-white border-[hsl(var(--lux-gold)/0.35)] focus-visible:ring-[hsl(var(--lux-gold))]"
                value={search}
                onChange={e => setSearch(e.target.value)}
                data-testid="events-filter-search-input"
              />
            </div>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-80 rounded-2xl" />)}
            </div>
          ) : filtered.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((event, idx) => {
                const eventDate = new Date(event.date);
                const ticketTypes = event.ticket_types || {};
                const lastEventDay = [...(event.event_days || [])].filter(Boolean).sort().pop();   // past only after the end of the LAST day
                const isPast = (lastEventDay ? new Date(`${lastEventDay}T23:59:59`) : eventDate) < new Date();
                return (
                  <motion.div key={event.event_id} {...fadeInUp} transition={{ delay: idx * 0.06, duration: 0.5 }}>
                    <Link to={`/events/${event.event_id}`}>
                      <motion.div whileHover={{ y: -6 }} transition={{ type: 'spring', stiffness: 300, damping: 20 }} className="h-full">
                        <Card className={`lux-ornate-card h-full hover:shadow-[var(--lux-shadow-2)] transition-shadow cursor-pointer overflow-hidden group ${isPast ? 'opacity-75' : ''}`} data-testid="event-card">
                          {(event.flyer_preview_url || event.image_url) && (
                            <div className="h-56 overflow-hidden bg-[hsl(var(--muted))] flex items-center justify-center" data-testid="event-card-flyer">
                              <img src={event.flyer_preview_url ? `${BACKEND}${event.flyer_preview_url}` : event.image_url} alt={event.title} className={`w-full h-full ${event.flyer_preview_url ? 'object-contain' : 'object-cover'} group-hover:scale-105 transition-transform duration-500`} />
                            </div>
                          )}
                          <CardContent className="p-6">
                            <div className="flex items-center gap-2 mb-3">
                              <Badge variant="outline" className="text-xs bg-[hsl(var(--lux-gold-soft))] border-[hsl(var(--lux-gold)/0.5)] text-[hsl(var(--lux-royal))] font-medium">
                                <Calendar className="w-3 h-3 mr-1" />
                                {eventDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </Badge>
                              {isPast && <Badge variant="secondary" className="text-xs">Past</Badge>}
                            </div>
                            <h3 className="text-2xl font-bold text-[hsl(var(--lux-royal))] mb-2 leading-snug" style={{ fontFamily: 'Cormorant Garamond' }}>{event.title}</h3>
                            <p className="text-sm text-[hsl(var(--lux-ink)/0.7)] line-clamp-2 mb-3">{event.description}</p>
                            {event.venue && (
                              <div className="flex items-center gap-1.5 text-xs text-[hsl(var(--lux-ink)/0.65)] mb-3">
                                <MapPin className="w-3.5 h-3.5 text-[hsl(var(--lux-gold))]" /> {event.venue}
                              </div>
                            )}
                            <div className="flex items-center gap-2 flex-wrap mb-4">
                              {event.is_multi_day && event.event_days?.length > 1 && (
                                <Badge className="bg-[hsl(var(--lux-royal))] text-white text-xs">{event.event_days.length}-Day Event</Badge>
                              )}
                              {(event.ticket_categories || []).filter(c => c.visible !== false).slice(0, 3).map(c => (
                                <Badge key={c.id} variant="outline" className="text-xs border-[hsl(var(--lux-gold)/0.5)] text-[hsl(var(--lux-ink)/0.8)]">{c.name}: £{c.price}{event.is_multi_day ? '/day' : ''}</Badge>
                              ))}
                              {ticketTypes.member && <Badge className="bg-[hsl(var(--lux-gold-soft))] text-[hsl(var(--lux-royal))] text-xs border border-[hsl(var(--lux-gold)/0.4)]">Member: £{ticketTypes.member.price}</Badge>}
                              {ticketTypes.non_member && <Badge variant="outline" className="text-xs border-[hsl(var(--lux-gold)/0.5)] text-[hsl(var(--lux-ink)/0.8)]">Standard: £{ticketTypes.non_member.price}</Badge>}
                            </div>
                            {!isPast && (
                              <Button size="sm" className="w-full bg-[hsl(var(--lux-royal))] text-white hover:bg-[hsl(var(--lux-royal-2))] shadow-md transition-all" data-testid="event-card-view-details-button">
                                View Details <ArrowRight className="w-3 h-3 ml-1" />
                              </Button>
                            )}
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <Card className="lux-ornate-card text-center p-12" data-testid="events-empty-state">
              <Calendar className="w-12 h-12 text-[hsl(var(--lux-gold))] mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-[hsl(var(--lux-royal))] mb-2" style={{ fontFamily: 'Cormorant Garamond' }}>
                {tab === 'upcoming' ? 'No upcoming events' : 'No past events'}
              </h3>
              <p className="text-[hsl(var(--lux-ink)/0.7)]">{tab === 'upcoming' ? 'New celebrations are being planned — check back soon!' : 'No past events found.'}</p>
            </Card>
          )}
        </div>
      </section>
    </div>
  );
}
